import { useEffect, useState } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { supabase } from './lib/supabase'
import { User } from './types'
import LoginScreen from './components/LoginScreen'
import SetNewPasswordScreen from './components/SetNewPasswordScreen'
import PublicInvoiceView from './components/PublicInvoiceView'
import SellerPortal from './components/SellerPortal'
import ErrorBoundary from './components/ErrorBoundary'
import Dashboard from './components/Dashboard'
/* Winnie, 15 Sep: one save confirmation, everywhere. Mounted at the root rather than inside
   the dashboard shell so the seller and buyer pages — which render outside it — get the same
   confirmation when they save something. */
import ToastHost from './components/Toast'
import './App.css'

function App() {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()

  // A Supabase recovery link creates a real session whose only legitimate use is
  // setting a new password. Without this the app would treat the user as signed
  // in and drop them on the dashboard instead of the set-password screen.
  const isRecoveryRoute = location.pathname.startsWith('/auth/reset-password')

  // The buyer-facing invoice page. Buyers are not DirectAuto users, so this
  // route renders whether or not anyone is signed in and never touches the
  // dashboard shell.
  const isPublicInvoice = location.pathname.startsWith('/invoice/')

  // The seller-facing pages. Sellers are not DirectAuto users either, and the
  // links we text and email them have to open something — until now
  // /sign-bos and /upload-dd were not routes at all, so a seller following the
  // Vehicle Purchase Agreement SMS landed on the login screen for a system they have no
  // account for. Both old paths are kept as aliases so any link already in
  // somebody's inbox still works.
  const isSellerPortal =
    location.pathname.startsWith('/seller/') ||
    location.pathname.startsWith('/sign-bos/') ||
    location.pathname.startsWith('/upload-dd/')

  useEffect(() => {
    // Check if user is already logged in
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.user) {
        const { data: profile, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', session.user.id)
          .single()

        // is_active was written by Deactivate user and read nowhere, so a
        // deactivated account kept signing in and kept its existing session.
        // A failed read is not a deactivation, so it signs nobody out.
        if (!error) {
          if (profile && profile.is_active !== false) {
            setUser(profile)
          } else if (profile) {
            await supabase.auth.signOut()
          }
        }
      }
      setLoading(false)
    }

    getSession()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        const { data: profile, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', session.user.id)
          .single()

        /**
         * NOT BEING ABLE TO READ THE PROFILE IS NOT THE SAME AS NOT BEING ALLOWED IN.
         *
         * This handler runs on every auth event, TOKEN_REFRESHED included - roughly hourly,
         * and again whenever a sleeping tab wakes. The error from this query used to be
         * discarded, so one failed read (a network blip, a wake from sleep, a rate limit)
         * left profile null, fell through to setUser(null), and threw a signed-in person
         * back to the login screen with their session still perfectly valid. Reloading
         * put them straight back in, which is the tell: the session was never gone, only
         * the app's copy of who they were.
         *
         * So a failed lookup changes nothing. Only a lookup that SUCCEEDS and says the
         * account is missing or deactivated may sign anyone out.
         */
        if (error) return

        if (profile && profile.is_active !== false) {
          setUser(profile)
        } else {
          setUser(null)
          if (profile) await supabase.auth.signOut()
        }
      } else {
        setUser(null)
      }
    })

    return () => subscription?.unsubscribe()
  }, [])

  if (loading) {
    return (
      <div className="splash">
        <div className="splash-content">
          <div className="splash-logo">DA</div>
          <p>Getting everything ready...</p>
          <div className="splash-loader"></div>
        </div>
      </div>
    )
  }

  if (isPublicInvoice) {
    return (
      <ErrorBoundary onReset={() => window.location.reload()}>
        <Routes>
          <Route path="/invoice/:token" element={<PublicInvoiceView />} />
        </Routes>
        <ToastHost />
      </ErrorBoundary>
    )
  }

  if (isSellerPortal) {
    return (
      <ErrorBoundary onReset={() => window.location.reload()}>
        <Routes>
          <Route path="/seller/:token" element={<SellerPortal />} />
          <Route path="/sign-bos/:token" element={<SellerPortal />} />
          <Route path="/upload-dd/:token" element={<SellerPortal />} />
        </Routes>
        <ToastHost />
      </ErrorBoundary>
    )
  }

  if (isRecoveryRoute) {
    return (
      <div className="app-shell">
        <ErrorBoundary onReset={() => navigate('/login')}>
          <SetNewPasswordScreen onDone={() => navigate('/login', { replace: true })} />
        </ErrorBoundary>
        <ToastHost />
      </div>
    )
  }

  return (
    <>
      <div className="mobile-only-notice">
        <div className="mobile-only-card">
          <div className="mobile-only-logo">DA</div>
          <h1>DirectAuto is desktop-only</h1>
          <p>The platform is optimised for a desktop browser. Please open <b>{window.location.host}</b> on a laptop or desktop to sign in and manage deals.</p>
        </div>
      </div>
      <div className="app-shell">
        <ErrorBoundary onReset={() => window.location.reload()}>
          {user ? (
            <Routes>
              {/* Signing in while sitting on /login should not leave you there. */}
              <Route path="/login" element={<Navigate to="/today" replace />} />
              <Route path="/" element={<Navigate to="/today" replace />} />
              <Route path="*" element={<Dashboard user={user} />} />
            </Routes>
          ) : (
            <Routes>
              <Route
                path="*"
                element={
                  <LoginScreen
                    onLoginSuccess={(u) => {
                      setUser(u)
                      // Send them where they were trying to go, so a link from an
                      // email survives the login detour.
                      const target =
                        location.pathname && location.pathname !== '/' && location.pathname !== '/login'
                          ? location.pathname + location.search
                          : '/today'
                      navigate(target, { replace: true })
                    }}
                  />
                }
              />
            </Routes>
          )}
        </ErrorBoundary>
        <ToastHost />
      </div>
    </>
  )
}

export default App
